import { useQuery } from '@tanstack/react-query';
import { Link } from 'wouter';
import { api } from '../lib/api';
import { applicationLabels, money, shortDate } from '../lib/format';
import type { CustomerApplication, ListResponse } from '../types';
import {
  Card,
  EmptyState,
  ErrorNotice,
  LoadingScreen,
  PageHeader,
  StatusBadge,
} from '../components/ui';

export function CustomerApplicationsPage() {
  const query = useQuery({
    queryKey: ['customer', 'applications'],
    queryFn: () => api<ListResponse<CustomerApplication>>('/customer/applications?limit=50'),
  });

  if (query.isPending) return <LoadingScreen label="Смотрим ваши заявки" />;
  if (query.error) return <ErrorNotice error={query.error} />;

  return (
    <>
      <PageHeader eyebrow="Кабинет заказчика" title="Мои заявки" />
      {query.data.items.length === 0 ? (
        <EmptyState
          title="Заявок пока нет"
          text="Отправите заявку художнику — здесь будет видно, что он ответил."
        />
      ) : (
        <div className="stack">
          {query.data.items.map((application) => (
            <Card key={application.id} className="stack stack--compact">
              <div className="card-row">
                <div>
                  <p className="eyebrow">{application.publicNumber}</p>
                  <strong>{application.title}</strong>
                </div>
                <StatusBadge
                  tone={
                    application.status === 'ACCEPTED'
                      ? 'success'
                      : application.status === 'REJECTED'
                        ? 'danger'
                        : application.status === 'NEEDS_INFO'
                          ? 'warning'
                          : 'neutral'
                  }
                >
                  {applicationLabels[application.status]}
                </StatusBadge>
              </div>
              <div className="card-row">
                <span className="meta">{application.artist.displayName}</span>
                <span className="meta">{shortDate(application.desiredDeadline)}</span>
              </div>
              {application.budget && (
                <span className="meta">
                  Бюджет {money(application.budget, application.currency)}
                </span>
              )}
              {application.order && (
                <Link href={`/my/orders/${application.order.id}`} className="plain-link">
                  <span className="meta">Открыть заказ {application.order.publicNumber} →</span>
                </Link>
              )}
            </Card>
          ))}
        </div>
      )}
    </>
  );
}
